/**
 * Vista previa de actividad antes de desplegar
 * Muestra tarea completa, objetivos y permite desplegar o ajustar
 */

import React, { useState } from 'react';
import { teacherActivityService } from '../../services/teacherActivityService';
import { TeacherActivity } from '../../types';
import './TeacherActivityPreview.css';

interface TeacherActivityPreviewProps {
  activity: TeacherActivity;
  onAdjust: () => void;
  onDeployed: (activity: TeacherActivity) => void;
  onCancel: () => void;
}

const TeacherActivityPreview: React.FC<TeacherActivityPreviewProps> = ({
  activity,
  onAdjust,
  onDeployed,
  onCancel,
}) => {
  const [deploying, setDeploying] = useState(false);
  const [classroomId, setClassroomId] = useState<number | null>(activity.classroom_id || null);
  const [level, setLevel] = useState<string>(activity.level || '');

  const handleDeploy = async () => {
    if (!activity.id) {
      alert('Error: La actividad no tiene ID.');
      return;
    }

    // Si se modificó classroom_id o level, actualizar primero
    if (
      classroomId !== activity.classroom_id ||
      level !== (activity.level || '')
    ) {
      const updated = await teacherActivityService.updateActivity(activity.id, {
        classroom_id: classroomId,
        level: level.trim() || null,
      });

      if (!updated) {
        alert('Error al actualizar la actividad. Por favor, intenta de nuevo.');
        return;
      }
    }

    setDeploying(true);
    try {
      const deployed = await teacherActivityService.deployActivity(activity.id);
      if (deployed) {
        onDeployed(deployed);
      } else {
        alert('Error al desplegar la actividad. Por favor, intenta de nuevo.');
      }
    } catch (error) {
      console.error('[TeacherActivityPreview] Error al desplegar:', error);
      alert('Error al desplegar la actividad. Por favor, intenta de nuevo.');
    } finally {
      setDeploying(false);
    }
  };

  const handleCopyUrl = async (tacheId: string) => {
    const url = teacherActivityService.generateActivityUrl(tacheId);
    try {
      await navigator.clipboard.writeText(url);
      alert('URL copiada al portapapeles: ' + url);
    } catch (error) {
      console.error('[TeacherActivityPreview] Error al copiar URL:', error);
      prompt('Copia esta URL:', url);
    }
  };

  return (
    <div className="teacher-activity-preview">
      <div className="preview-header">
        <h2>Vista Previa de Actividad</h2>
        <p className="preview-subtitle">Revisa la actividad antes de desplegarla</p>
      </div>

      <div className="preview-content">
        <div className="preview-section">
          <h3>Tarea</h3>
          <div className="preview-text">{activity.task_description}</div>
        </div>

        <div className="preview-section">
          <h3>Objetivos ({activity.objectives.length})</h3>
          <div className="objectives-preview">
            {activity.objectives.map((objective, index) => (
              <div key={index} className="objective-preview-item">
                <div className="objective-preview-header">
                  <span className="objective-number">Objetivo {index + 1}</span>
                </div>
                <div className="objective-preview-description">
                  <strong>Descripción:</strong> {objective.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="preview-section">
          <h3>Configuración</h3>
          <div className="preview-config">
            <div className="config-field">
              <label htmlFor="preview-classroom-id">
                <strong>ID del Aula (Opcional)</strong>
                <span className="field-hint">
                  Si especificas un aula, solo estudiantes de esa aula verán esta actividad.
                  Si lo dejas vacío, será una actividad general visible para todos.
                </span>
              </label>
              <input
                id="preview-classroom-id"
                type="number"
                value={classroomId || ''}
                onChange={(e) =>
                  setClassroomId(e.target.value ? parseInt(e.target.value) : null)
                }
                placeholder="Ejemplo: 12345"
                min="1"
              />
            </div>
            <div className="config-field">
              <label htmlFor="preview-level">
                <strong>Nivel Académico (Opcional)</strong>
              </label>
              <input
                id="preview-level"
                type="text"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                placeholder="Ejemplo: A1.1"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="preview-actions">
        <button className="action-button adjust" onClick={onAdjust} disabled={deploying}>
          Ajustar Actividad
        </button>
        <button className="action-button cancel" onClick={onCancel} disabled={deploying}>
          Cancelar
        </button>
        <button
          className="action-button deploy"
          onClick={handleDeploy}
          disabled={deploying}
        >
          {deploying ? 'Desplegando...' : 'Desplegar Actividad'}
        </button>
      </div>
    </div>
  );
};

export default TeacherActivityPreview;
